import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';

const HOST_API = process.env.REACT_APP_URL;

const INTERES_EMPTY = {
    interesBarrioId: 0,
    porcentaje: null,
    fechaInicio: null,
    fechaFin: null,
    activo: true,
};

const fmtFecha = (v) => v ? new Date(v).toLocaleDateString('es-AR') : '—';
const fmtPorcentaje = (v) => v != null ? `${(Number(v) * 100).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%` : '—';

const estadoBadge = (row) => (
    <span style={{
        background: row.activo ? '#ecfdf3' : '#fef3f2',
        color: row.activo ? '#12b76a' : '#f04438',
        border: `1px solid ${row.activo ? '#a9efc5' : '#fecdca'}`,
        fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
    }}>
        {row.activo ? 'Activo' : 'Inactivo'}
    </span>
);

const Intereses = () => {
    const toast = useRef(null);
    const { barrio } = useContext(BarrioContext);
    const { administracionId, usuarioId } = useAuth();

    const [intereses, setIntereses] = useState([]);
    const [loading, setLoading] = useState(false);

    const [modalVisible, setModalVisible] = useState(false);
    const [form, setForm] = useState({ ...INTERES_EMPTY });
    const [submitting, setSubmitting] = useState(false);

    const fetchIntereses = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const { data } = await axios.get(`${HOST_API}api/interesesPorBarrio/GetInteresesPorBarrio`, {
                params: { barrioId: barrio.id, administracionId },
            });
            setIntereses(data ?? []);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los intereses', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, administracionId]);

    useEffect(() => { fetchIntereses(); }, [fetchIntereses]);

    const nuevoInteres = () => {
        setForm({ ...INTERES_EMPTY });
        setModalVisible(true);
    };

    const editarInteres = (row) => {
        setForm({
            interesBarrioId: row.interesBarrioId,
            porcentaje: Number(row.porcentaje) * 100,
            fechaInicio: row.fechaInicio ? new Date(row.fechaInicio) : null,
            fechaFin: row.fechaFin ? new Date(row.fechaFin) : null,
            activo: row.activo,
        });
        setModalVisible(true);
    };

    const guardarInteres = async () => {
        if (form.porcentaje == null) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese el porcentaje', life: 3000 }); return; }
        if (!form.fechaInicio)       { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese la fecha de inicio', life: 3000 }); return; }

        setSubmitting(true);
        try {
            const payload = {
                interesBarrioId: form.interesBarrioId,
                barrioId: barrio.id,
                administracionId,
                usuarioId,
                porcentaje: Number(form.porcentaje) / 100,
                fechaInicio: form.fechaInicio,
                fechaFin: form.fechaFin,
                activo: form.activo,
            };
            await axios.post(`${HOST_API}api/interesesPorBarrio/SaveInteresPorBarrio`, payload);
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Interés guardado correctamente', life: 3000 });
            setModalVisible(false);
            fetchIntereses();
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.errorDisplay || 'No se pudo guardar el interés', life: 4000 });
        } finally {
            setSubmitting(false);
        }
    };

    const eliminarInteres = (row) => {
        confirmDialog({
            message: `¿Eliminar el interés del ${fmtFecha(row.fechaInicio)} (${fmtPorcentaje(row.porcentaje)})?`,
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.delete(`${HOST_API}api/interesesPorBarrio/DeleteInteresPorBarrio`, {
                        params: { interesBarrioId: row.interesBarrioId, administracionId, barrioId: barrio.id },
                    });
                    toast.current.show({ severity: 'success', summary: 'Eliminado', detail: 'Interés eliminado', life: 3000 });
                    fetchIntereses();
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar el interés', life: 3000 });
                }
            },
        });
    };

    const accionesTemplate = (row) => (
        <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
            <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-sm"
                tooltip="Editar" tooltipOptions={{ position: 'top' }}
                onClick={() => editarInteres(row)} />
            <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger p-button-sm"
                tooltip="Eliminar" tooltipOptions={{ position: 'top' }}
                onClick={() => eliminarInteres(row)} />
        </div>
    );

    return (
        <>
            <h4>Intereses</h4>
            <br />
            <Toast ref={toast} />
            <ConfirmDialog />

<LoadingOverlay visible={loading} />

            <div className="flex justify-content-end mb-3">
                <Button label="Nuevo Interés" icon="pi pi-plus" className="p-button-sm" onClick={nuevoInteres} />
            </div>

            <div className="grid">
                <div className="col-12">
                    <DataTable value={intereses} paginator rows={15} rowsPerPageOptions={[10, 15, 25, 50]}
                        showGridlines responsiveLayout="scroll"
                        emptyMessage="No hay intereses registrados para este barrio"
                        className="p-datatable-gridlines bg-white"
                        sortField="fechaInicio" sortOrder={-1}>
                        <Column field="porcentaje" header="Porcentaje" sortable body={(r) => fmtPorcentaje(r.porcentaje)} style={{ width: '10rem', textAlign: 'right' }} />
                        <Column field="fechaInicio" header="Fecha Inicio" sortable body={(r) => fmtFecha(r.fechaInicio)} style={{ width: '10rem' }} />
                        <Column field="fechaFin" header="Fecha Fin" sortable body={(r) => fmtFecha(r.fechaFin)} style={{ width: '10rem' }} />
                        <Column header="Estado" body={estadoBadge} style={{ width: '9rem', textAlign: 'center' }} />
                        <Column field="usuario" header="Usuario" sortable />
                        <Column field="fechaAlta" header="Fecha Alta" sortable body={(r) => fmtFecha(r.fechaAlta)} style={{ width: '10rem' }} />
                        <Column header="Acciones" body={accionesTemplate} style={{ width: '7rem', textAlign: 'center' }} />
                    </DataTable>
                </div>
            </div>

            <Dialog header={form.interesBarrioId ? 'Editar Interés' : 'Nuevo Interés'} visible={modalVisible} style={{ width: '440px' }} modal
                onHide={() => setModalVisible(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" className="p-button-text p-button-sm" onClick={() => setModalVisible(false)} disabled={submitting} />
                        <Button label="Guardar" icon="pi pi-check" className="p-button-sm" onClick={guardarInteres} loading={submitting} />
                    </div>
                }>
                <div className="grid p-fluid mt-1">
                    <div className="col-12">
                        <label style={lbl}>Porcentaje mensual (%) *</label>
                        <InputNumber value={form.porcentaje} onValueChange={(e) => setForm(f => ({ ...f, porcentaje: e.value }))}
                            minFractionDigits={2} maxFractionDigits={4} suffix=" %" className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Fecha Inicio *</label>
                        <Calendar value={form.fechaInicio} onChange={(e) => setForm(f => ({ ...f, fechaInicio: e.value }))}
                            dateFormat="dd/mm/yy" showIcon className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Fecha Fin</label>
                        <Calendar value={form.fechaFin} onChange={(e) => setForm(f => ({ ...f, fechaFin: e.value }))}
                            dateFormat="dd/mm/yy" showIcon showButtonBar className="p-inputtext-sm" />
                    </div>
                    {form.interesBarrioId > 0 && (
                        <div className="col-12 flex align-items-center gap-2 mt-2">
                            <Checkbox inputId="activo" checked={form.activo}
                                onChange={(e) => setForm(f => ({ ...f, activo: e.checked }))} />
                            <label htmlFor="activo">Activo</label>
                        </div>
                    )}
                </div>
            </Dialog>
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default Intereses;
